/**
 * Dashboard page component
 */

import { useNavigate } from 'react-router-dom';
import { StatsCard, DistributionCard, RecentActivity } from '../components/dashboard';
import {
  useDashboardStats,
  useBottlesByType,
  useBottlesByPriceRange,
  useBottlesByState,
  useBottlesByCountry,
  useRecentBottles,
  useRecentlyEmptiedBottles,
  useOrderedBottles
} from '../hooks/useDashboard';
import { formatCurrency } from '../utils/formatters';
import { PRICE_RANGES } from '../utils/constants';
import './Dashboard.css';

/**
 * Builds the /bottles query params that filter down to the bottles
 * represented by a clicked distribution chart segment.
 * @param {string} group - 'type' | 'price' | 'state' | 'country'
 * @param {{label: string, value: number}} item - The clicked segment
 * @returns {string} Query string (including leading '?')
 */
function buildBottlesFilterQuery(group, item) {
  const params = new URLSearchParams();

  if (group === 'type') {
    params.set('type', item.label);
  } else if (group === 'state') {
    params.set('state', item.label);
  } else if (group === 'country') {
    params.set('country', item.label);
  } else if (group === 'price') {
    const range = PRICE_RANGES.find(r => r.label === item.label);
    if (range) {
      params.set('priceMin', String(range.min));
      if (range.max != null) params.set('priceMax', String(range.max));
      params.set('priceLabel', range.label);
    }
  }

  return `?${params.toString()}`;
}

/**
 * Dashboard with collection metrics and charts
 * @returns {React.ReactElement} Page element
 */
export default function Dashboard() {
  const navigate = useNavigate();
  const { data: stats, isLoading: statsLoading } = useDashboardStats();
  const { data: bottlesByType } = useBottlesByType();
  const { data: bottlesByPriceRange } = useBottlesByPriceRange();
  const { data: bottlesByState } = useBottlesByState();
  const { data: bottlesByCountry } = useBottlesByCountry();
  const { data: recentBottles, isLoading: recentLoading } = useRecentBottles(7);
  const { data: emptiedBottles, isLoading: emptiedLoading } = useRecentlyEmptiedBottles(7);
  const { data: orderedBottles, isLoading: orderedLoading } = useOrderedBottles();

  const statusBreakdown = [
    { label: 'Ordered', value: stats?.ordered_count || 0 },
    { label: 'Unopened', value: stats?.unopened_count || 0 },
    { label: 'Opened', value: stats?.opened_count || 0 },
    { label: 'Empty', value: stats?.empty_count || 0 }
  ];

  const typeChartData = (bottlesByType || []).map(item => ({
    label: item.type,
    value: Number(item.count)
  }));

  const priceChartData = (bottlesByPriceRange || []).map(item => ({
    label: item.price_range,
    value: Number(item.count)
  }));

  const stateChartData = (bottlesByState || []).map(item => ({
    label: item.state,
    value: Number(item.count)
  }));

  const countryChartData = (bottlesByCountry || []).map(item => ({
    label: item.country,
    value: Number(item.count)
  }));

  const handleChartSegmentClick = (group, item) => {
    navigate(`/bottles${buildBottlesFilterQuery(group, item)}`);
  };

  return (
    <div className="dashboard">
      <h1 className="dashboard__title">Dashboard</h1>

      <div className="dashboard__stats">
        <StatsCard
          title="Total Bottles"
          value={statsLoading ? '-' : (stats?.unopened_count || 0) + (stats?.opened_count || 0)}
          totalValue={statsLoading ? null : stats?.total_bottles || 0}
          breakdown={statusBreakdown}
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 2h4" />
              <path d="M10 2v4l-2 2v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V8l-2-2V2" />
              <path d="M8 14h8" />
            </svg>
          }
        />

        <StatsCard
          title="Total Distilleries"
          value={statsLoading ? '-' : stats?.total_distilleries || 0}
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 21h18" />
              <path d="M5 21V7l8-4v18" />
              <path d="M19 21V11l-6-4" />
            </svg>
          }
        />

        <StatsCard
          title="Collection Value"
          value={statsLoading ? '-' : formatCurrency(stats?.total_value || 0)}
          subtitle="Total purchase price"
          secondaryValue={
            !statsLoading && stats?.estimated_value != null && stats.estimated_value !== stats.total_value
              ? formatCurrency(stats.estimated_value)
              : undefined
          }
          secondaryLabel="est. w/ avg cost"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          }
        />
      </div>

      <div className="dashboard__charts">
        <DistributionCard
          typeData={typeChartData}
          priceData={priceChartData}
          stateData={stateChartData}
          countryData={countryChartData}
          onSegmentClick={handleChartSegmentClick}
        />
      </div>

      <div className="dashboard__activity">
        <RecentActivity
          recentBottles={recentBottles}
          recentLoading={recentLoading}
          emptiedBottles={emptiedBottles}
          emptiedLoading={emptiedLoading}
          orderedBottles={orderedBottles}
          orderedLoading={orderedLoading}
        />
      </div>
    </div>
  );
}
